import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "wouter";
import { appShell } from "@/store";
import { projectsAtom, rainfallDataByPropertyAtom, selectedPropertyAtom } from "@/store/data";
import { showSuccessToastAtom } from "@/store/ui";

import { ReportDetailModal } from "./components/ReportDetailModal";
import { ReportFilters } from "./components/ReportFilters";
import { ReportTable } from "./components/ReportTable";
import { getDefaultFilters, useIrrigationReport } from "./hooks/useIrrigationReport";
import type { ReportItem } from "./types";
import { useRainfallHistory } from "./hooks/useRainfallHistory";
import dayjs from "dayjs";
import { RainfallSummary } from "./components/RainfallSummary";

function IrrigationPlanExecutionReportPage() {
  const setBackButton = useSetAtom(appShell.backButtonAtom);
  const [searchParams] = useSearchParams();
  const showSuccessToast = useSetAtom(showSuccessToastAtom);
  const projects = useAtomValue(projectsAtom);

  const rainfallSummary = useAtomValue(rainfallDataByPropertyAtom);
  const selectPropriety = useAtomValue(selectedPropertyAtom);
  const {result: rainfallResult, 
    error: rainfallError, 
    loading: rainfallLoading, 
    calculate: rainfallCalculate} = useRainfallHistory();

  // State management
  const [filters, setFilters] = useState(() => getDefaultFilters(searchParams));
  const [selectedItem, setSelectedItem] = useState<ReportItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  // Get project and available plans
  const project = useMemo(() => {
    return filters.projectId ? (projects.find((p) => p.id === filters.projectId) ?? null) : null;
  }, [filters.projectId, projects]);

  const availablePlans = useMemo(() => {
    if (!project?.irrigation_plans) return [];
    return project.irrigation_plans;
  }, [project]);

  // Custom hook for report data fetching
  const { reportData, loading, error, fetchReport } = useIrrigationReport({
    project,
    onSuccess: (hasData) => {
      if (!hasData) {
        showSuccessToast({ message: "Nenhum dado encontrado para os filtros selecionados", title: "" });
      } else {
        setFiltersCollapsed(true);
      }
    },
  });

  // Set back button
  useEffect(() => {
    setBackButton("/app/reports");
    return () => {
      setBackButton(false);
    };
  }, [setBackButton]);

  // Handle filter application
  const handleApplyFilters = useCallback(() => {
    fetchReport(filters);
    rainfallCalculate({
        id: rainfallSummary.details[0]?.deviceId,
        resolutionMm: selectPropriety?.rain_gauge_resolution_mm ?? 0.2
      },
      dayjs(filters.startDate).startOf('day').toISOString(), 
      // verificar se irei seguir com essa abordagem ou utilizar o format mesmo, visto que pode ter um "periodo personalizado"
      dayjs(filters.endDate).endOf('day').toISOString(),
    );
  }, [fetchReport, filters]);

  // Handle item click for detail view
  const handleItemClick = useCallback((item: ReportItem) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setShowDetailModal(false);
    setSelectedItem(null);
  }, []);

  return (
    <div className="min-h-full space-y-6 bg-gray-50 p-4 pb-20">
      {/* Header */}
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">Relatório de Execução</h1>
      </header>

      {/* Filters Section */}
      <ReportFilters
        filters={filters}
        setFilters={setFilters}
        projects={projects}
        availablePlans={availablePlans}
        error={error}
        loading={loading}
        collapsed={filtersCollapsed}
        onCollapse={setFiltersCollapsed}
        onApply={handleApplyFilters}
      />

      <RainfallSummary rainfallResult={rainfallResult} rainfallLoading={rainfallLoading} rainfallError={rainfallError} />

      {/* Results Section */}
      <ReportTable reportData={reportData} onItemClick={handleItemClick} />

      {/* Detail Modal */}
      <ReportDetailModal item={selectedItem} isOpen={showDetailModal} onClose={handleCloseModal} />
    </div>
  );
}

export default IrrigationPlanExecutionReportPage;
